import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Flag, X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const REPORT_REASONS = [
  { value: 'fake', label: 'Falsk profil', description: 'Bruger billeder af en anden person' },
  { value: 'inappropriate', label: 'Upassende indhold', description: 'Upassende billeder eller bio' },
  { value: 'harassment', label: 'Chikane', description: 'Truende eller chikanerende adfærd' },
  { value: 'spam', label: 'Spam', description: 'Reklamer eller svindel' },
  { value: 'other', label: 'Andet', description: 'Anden grund' }
];

const BlockReportModal = ({ isOpen, onClose, userId, userName, onBlocked }) => {
  const [mode, setMode] = useState('choose'); // 'choose', 'block', 'report'
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  const handleBlock = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/block`, { user_id: userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} er nu blokeret`);
      onBlocked?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke blokere bruger');
    } finally {
      setLoading(false);
    }
  };

  const handleReport = async () => {
    if (!reportReason) {
      toast.error('Vælg venligst en årsag');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/report`, {
        user_id: userId,
        reason: reportReason,
        details: reportDetails || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tak for din rapport. Vi gennemgår den snarest.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke rapportere bruger');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMode('choose');
    setReportReason('');
    setReportDetails('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-card rounded-2xl border border-border w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-display font-semibold text-foreground">
              {mode === 'choose' && 'Handlinger'}
              {mode === 'block' && 'Bloker Bruger'}
              {mode === 'report' && 'Rapporter Bruger'}
            </h3>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-4">
            {mode === 'choose' && (
              <div className="space-y-3">
                <button
                  onClick={() => setMode('block')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors text-left"
                  data-testid="block-option-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-destructive" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Bloker {userName}</p>
                    <p className="text-sm text-muted-foreground">I kan ikke længere se eller kontakte hinanden</p>
                  </div>
                </button>

                <button
                  onClick={() => setMode('report')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors text-left"
                  data-testid="report-option-btn"
                >
                  <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
                    <Flag className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Rapporter {userName}</p>
                    <p className="text-sm text-muted-foreground">Anmeld upassende adfærd til os</p>
                  </div>
                </button>
              </div>
            )}

            {mode === 'block' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-destructive/10 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0" />
                  <p className="text-sm text-foreground">
                    Når du blokerer {userName}, fjernes eventuelle matches og I kan ikke længere se eller kontakte hinanden.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setMode('choose')}
                    className="flex-1 rounded-full"
                  >
                    Annuller
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleBlock}
                    disabled={loading}
                    className="flex-1 rounded-full"
                    data-testid="confirm-block-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Bloker'}
                  </Button>
                </div>
              </div>
            )}

            {mode === 'report' && (
              <div className="space-y-4">
                <RadioGroup value={reportReason} onValueChange={setReportReason}>
                  {REPORT_REASONS.map((reason) => (
                    <div
                      key={reason.value}
                      className={`flex items-start gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${
                        reportReason === reason.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => setReportReason(reason.value)}
                    >
                      <RadioGroupItem value={reason.value} id={reason.value} />
                      <div className="flex-1">
                        <Label htmlFor={reason.value} className="font-medium cursor-pointer">
                          {reason.label}
                        </Label>
                        <p className="text-sm text-muted-foreground">{reason.description}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>

                <div>
                  <Label className="text-sm text-muted-foreground">
                    Yderligere detaljer (valgfrit)
                  </Label>
                  <Textarea
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    placeholder="Beskriv problemet..."
                    className="mt-2 rounded-xl resize-none"
                    rows={3}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setMode('choose')}
                    className="flex-1 rounded-full"
                  >
                    Tilbage
                  </Button>
                  <Button
                    onClick={handleReport}
                    disabled={loading || !reportReason}
                    className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                    data-testid="submit-report-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Rapport'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BlockReportModal;
